/**
 * NATEX Opportunity Score - Country Indicator Data
 *
 * This file contains the actual indicator values for countries
 * used in NATEX Score calculations.
 *
 * Data sources:
 * - World Bank Open Data
 * - IMF World Economic Outlook
 * - UN Population Division
 * - ITU (International Telecommunication Union)
 * - World Governance Indicators
 * - B-Ready (World Bank Business Ready)
 * - Institute for Economics & Peace
 * - NATEX proprietary research
 */

import { CountryIndicatorData, ScoreAdjustment } from '../services/scoring/types';

// ============================================================================
// UZBEKISTAN - PRIMARY COUNTRY DATA (ALL 51 INDICATORS)
// ============================================================================

export const UZBEKISTAN_DATA: CountryIndicatorData = {
  countryCode: 'UZB',
  countryName: 'Uzbekistan',
  asOfDate: new Date('2025-12-15'),
  indicators: {
    // =========================================================================
    // PILLAR 1: GROWTH DRIVERS (15 indicators) - Target: ~88
    // Updated December 2025 with verified data
    // =========================================================================

    // Factor 1.1: Economic Momentum (5 indicators)
    GDP_GROWTH_5Y: 6.2,              // 5-year average GDP growth (2020-2024 avg) → Score 8
    GDP_GROWTH_ACCELERATION: 1.4,    // 7.6% current vs 6.2% 3Y avg → Score 8
    GDP_GROWTH_STABILITY: 1.5,       // Std dev of growth → Score 8 (stable)
    SECTOR_DIVERSIFICATION: 0.25,    // HHI index → Score 8 (diversified)
    PRODUCTIVITY_GROWTH: 4.2,        // Labor productivity growth % → Score 10

    // Factor 1.2: Demographic Dividend (5 indicators)
    WORKING_AGE_POP: 67,             // % population 15-64 → Score 8
    MEDIAN_AGE: 27,                  // Median age (sweet spot 25-35) → Score 10!
    POPULATION_GROWTH: 1.65,         // Annual population growth % → Score 10 (optimal)
    URBANIZATION_RATE: 51,           // Urban population % → Score 10 (sweet spot)
    DEPENDENCY_RATIO: 49,            // Dependents to working age ratio → Score 10

    // Factor 1.3: Digital Readiness (5 indicators)
    INTERNET_PENETRATION: 78,        // % population using internet → Score 8
    MOBILE_SUBSCRIPTIONS: 108,       // Per 100 people → Score 8
    DIGITAL_PAYMENTS_GROWTH: 45,     // YoY growth in digital payments → Score 8
    TECH_SECTOR_SIZE: 4.2,           // ICT as % of GDP → Score 6
    ECOMMERCE_PENETRATION: 9,        // Online retail as % of total → Score 6

    // =========================================================================
    // PILLAR 2: INVESTMENT CLIMATE (12 indicators) - Target: ~82
    // =========================================================================

    // Factor 2.1: Policy Environment (4 indicators)
    REGULATORY_QUALITY: 48,          // WGI percentile (improving) → Score 4
    GOVERNMENT_EFFECTIVENESS: 45,    // WGI percentile → Score 4
    POLICY_CONTINUITY: 8.0,          // NATEX assessment 1-10 → Score 8
    INVESTOR_PROTECTION: 72,         // B-Ready score → Score 8

    // Factor 2.2: Market Access (4 indicators)
    MARKET_SIZE_GDP: 115,            // Nominal GDP $115B → Score 8
    TRADE_OPENNESS: 68,              // Trade as % of GDP → Score 6
    FTA_NETWORK: 8,                  // Number of FTAs → Score 6
    REGIONAL_HUB_POTENTIAL: 8.5,     // NATEX score 1-10 → Score 8

    // Factor 2.3: Ease of Operations (4 indicators)
    DAYS_START_BUSINESS: 5,          // Days to register business → Score 10!
    TAX_BURDEN: 32,                  // Total tax rate % of profits → Score 8
    INFRASTRUCTURE_QUALITY: 2.9,     // LPI infrastructure score → Score 6
    TERTIARY_ENROLLMENT: 32,         // Tertiary education enrollment % → Score 6

    // =========================================================================
    // PILLAR 3: CATALYST PIPELINE (9 indicators) - Target: ~95
    // This is Uzbekistan's strongest pillar!
    // =========================================================================

    // Factor 3.1: Reform Momentum (3 indicators)
    REFORM_COUNT_2Y: 15,             // Significant reforms in 2 years → Score 10!
    REFORM_QUALITY: 8.8,             // NATEX quality assessment 1-10 → Score 10!
    REFORM_PIPELINE: 9.0,            // Pipeline strength 1-10 → Score 10!

    // Factor 3.2: FDI Pipeline (3 indicators)
    ANNOUNCED_FDI: 11.9,             // Record $11.9B FDI 2024 → Score 10!
    PIPELINE_CONVERSION: 75,         // % of announced deals completed → Score 10!
    INVESTOR_SENTIMENT: 8.2,         // Investor confidence index 1-10 → Score 8

    // Factor 3.3: Event Calendar (3 indicators)
    RATING_UPGRADE_POTENTIAL: 8.8,   // Likelihood of upgrade (just had 2!) → Score 10!
    ACCESSION_EVENTS: 9.2,           // WTO 91% complete, Mar 2026 → Score 10!
    POLITICAL_CALENDAR: 8.0,         // Political calendar impact → Score 10

    // =========================================================================
    // PILLAR 4: RISK PROFILE (9 indicators) - Target: ~76
    // Note: Higher score = LOWER risk
    // =========================================================================

    // Factor 4.1: Political Stability (3 indicators)
    POLITICAL_STABILITY_WGI: 55,     // WGI percentile → Score 6
    CONFLICT_RISK: 62,               // GPI percentile → Score 8
    GEOPOLITICAL_RISK: 7.0,          // NATEX score → Score 8

    // Factor 4.2: Macro Stability (3 indicators)
    INFLATION_CPI: 7.5,              // CPI inflation % (falling!) → Score 6
    CURRENCY_VOLATILITY: 11,         // 1Y FX volatility % → Score 8
    EXTERNAL_DEBT_GDP: 45,           // External debt as % GDP → Score 8

    // Factor 4.3: External Vulnerability (3 indicators)
    RESERVE_ADEQUACY: 11,            // $49.7B = 11 months cover → Score 10!
    CURRENT_ACCOUNT: -4.5,           // Current account as % GDP → Score 4
    SHORT_TERM_DEBT_RESERVES: 32,    // Short-term debt as % reserves → Score 8

    // =========================================================================
    // PILLAR 5: VALUATION SIGNAL (6 indicators) - Target: ~90
    // =========================================================================

    // Factor 5.1: Relative Valuation (3 indicators)
    FDI_GDP_VS_PEERS: 155,           // FDI/GDP relative to peer avg % → Score 10!
    GROWTH_VS_RATING: 18,            // Strongly underrated → Score 10!
    COST_ADVANTAGE: 8.0,             // Labor cost vs productivity → Score 8

    // Factor 5.2: Timing Indicator (3 indicators)
    SCORE_MOMENTUM: 5.0,             // NATEX score rising → Score 10!
    CATALYST_PROXIMITY: 75,          // ~75 days to WTO (Mar 2026) → Score 8
    INVESTOR_POSITIONING: 8.0,       // Contrarian opportunity → Score 10!
  },
};

// ============================================================================
// UZBEKISTAN ADJUSTMENTS
// ============================================================================

export const UZBEKISTAN_ADJUSTMENTS: ScoreAdjustment[] = [
  {
    id: 'sp_upgrade_2025',
    type: 'RATING_ACTION',
    description: 'S&P upgrade to BB (Nov 2025) - positive momentum signal',
    impact: 2,
    validFrom: new Date('2025-11-21'),
    validTo: new Date('2026-05-21'),
    source: 'S&P Global Ratings',
  },
  {
    id: 'fitch_upgrade_2025',
    type: 'RATING_ACTION',
    description: 'Fitch upgrade to BB (Jun 2025) - credit improvement',
    impact: 1.5,
    validFrom: new Date('2025-06-26'),
    validTo: new Date('2026-01-26'),
    source: 'Fitch Ratings',
  },
  {
    id: 'wto_proximity',
    type: 'EXCEPTIONAL_REFORM',
    description: 'WTO accession proximity boost (Mar 2026 target)',
    impact: 3,
    validFrom: new Date('2025-09-01'),
    validTo: new Date('2026-06-01'),
    source: 'WTO',
  },
];

// ============================================================================
// PEER COUNTRIES - SIMPLIFIED DATA
// ============================================================================

export const KAZAKHSTAN_DATA: CountryIndicatorData = {
  countryCode: 'KAZ',
  countryName: 'Kazakhstan',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 4.2,
    GDP_GROWTH_ACCELERATION: -0.5,
    GDP_GROWTH_STABILITY: 2.5,
    SECTOR_DIVERSIFICATION: 0.45,
    PRODUCTIVITY_GROWTH: 2.5,
    WORKING_AGE_POP: 66,
    MEDIAN_AGE: 31,
    POPULATION_GROWTH: 1.3,
    URBANIZATION_RATE: 58,
    DEPENDENCY_RATIO: 58,
    INTERNET_PENETRATION: 85,
    MOBILE_SUBSCRIPTIONS: 140,
    DIGITAL_PAYMENTS_GROWTH: 35,
    TECH_SECTOR_SIZE: 4.0,
    ECOMMERCE_PENETRATION: 10,

    // Investment Climate
    REGULATORY_QUALITY: 55,
    GOVERNMENT_EFFECTIVENESS: 48,
    POLICY_CONTINUITY: 7.0,
    INVESTOR_PROTECTION: 70,
    MARKET_SIZE_GDP: 245,
    TRADE_OPENNESS: 62,
    FTA_NETWORK: 12,
    REGIONAL_HUB_POTENTIAL: 7.0,
    DAYS_START_BUSINESS: 5,
    TAX_BURDEN: 28,
    INFRASTRUCTURE_QUALITY: 3.0,
    TERTIARY_ENROLLMENT: 65,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 6,
    REFORM_QUALITY: 6.5,
    REFORM_PIPELINE: 6.0,
    ANNOUNCED_FDI: 4.0,
    PIPELINE_CONVERSION: 65,
    INVESTOR_SENTIMENT: 6.5,
    RATING_UPGRADE_POTENTIAL: 5.0,
    ACCESSION_EVENTS: 3.0,
    POLITICAL_CALENDAR: 6.0,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 45,
    CONFLICT_RISK: 55,
    GEOPOLITICAL_RISK: 5.5,
    INFLATION_CPI: 7.8,
    CURRENCY_VOLATILITY: 15,
    EXTERNAL_DEBT_GDP: 85,
    RESERVE_ADEQUACY: 6,
    CURRENT_ACCOUNT: 2.0,
    SHORT_TERM_DEBT_RESERVES: 45,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 90,
    GROWTH_VS_RATING: 5,
    COST_ADVANTAGE: 6.0,
    SCORE_MOMENTUM: 1.5,
    CATALYST_PROXIMITY: 180,
    INVESTOR_POSITIONING: 5.0,
  },
};

// ============================================================================
// KYRGYZSTAN - CENTRAL ASIAN PEER
// ============================================================================

export const KYRGYZSTAN_DATA: CountryIndicatorData = {
  countryCode: 'KGZ',
  countryName: 'Kyrgyzstan',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 4.2,
    GDP_GROWTH_ACCELERATION: 0.8,
    GDP_GROWTH_STABILITY: 3.5,
    SECTOR_DIVERSIFICATION: 0.38,
    PRODUCTIVITY_GROWTH: 2.8,
    WORKING_AGE_POP: 64,
    MEDIAN_AGE: 26,
    POPULATION_GROWTH: 1.8,
    URBANIZATION_RATE: 37,
    DEPENDENCY_RATIO: 56,
    INTERNET_PENETRATION: 68,
    MOBILE_SUBSCRIPTIONS: 125,
    DIGITAL_PAYMENTS_GROWTH: 38,
    TECH_SECTOR_SIZE: 2.5,
    ECOMMERCE_PENETRATION: 5,

    // Investment Climate
    REGULATORY_QUALITY: 35,
    GOVERNMENT_EFFECTIVENESS: 28,
    POLICY_CONTINUITY: 5.0,
    INVESTOR_PROTECTION: 50,
    MARKET_SIZE_GDP: 12,
    TRADE_OPENNESS: 95,
    FTA_NETWORK: 6,
    REGIONAL_HUB_POTENTIAL: 5.5,
    DAYS_START_BUSINESS: 10,
    TAX_BURDEN: 29,
    INFRASTRUCTURE_QUALITY: 2.3,
    TERTIARY_ENROLLMENT: 45,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 4,
    REFORM_QUALITY: 5.0,
    REFORM_PIPELINE: 5.0,
    ANNOUNCED_FDI: 0.5,
    PIPELINE_CONVERSION: 55,
    INVESTOR_SENTIMENT: 5.0,
    RATING_UPGRADE_POTENTIAL: 4.5,
    ACCESSION_EVENTS: 3.0,
    POLITICAL_CALENDAR: 5.5,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 25,
    CONFLICT_RISK: 42,
    GEOPOLITICAL_RISK: 5.0,
    INFLATION_CPI: 11.5,
    CURRENCY_VOLATILITY: 18,
    EXTERNAL_DEBT_GDP: 65,
    RESERVE_ADEQUACY: 4,
    CURRENT_ACCOUNT: -8.0,
    SHORT_TERM_DEBT_RESERVES: 35,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 85,
    GROWTH_VS_RATING: 5,
    COST_ADVANTAGE: 7.5,
    SCORE_MOMENTUM: 1.5,
    CATALYST_PROXIMITY: 150,
    INVESTOR_POSITIONING: 5.5,
  },
};

// ============================================================================
// TAJIKISTAN - CENTRAL ASIAN PEER
// ============================================================================

export const TAJIKISTAN_DATA: CountryIndicatorData = {
  countryCode: 'TJK',
  countryName: 'Tajikistan',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 6.8,
    GDP_GROWTH_ACCELERATION: 0.5,
    GDP_GROWTH_STABILITY: 2.8,
    SECTOR_DIVERSIFICATION: 0.42,
    PRODUCTIVITY_GROWTH: 3.5,
    WORKING_AGE_POP: 60,
    MEDIAN_AGE: 23,
    POPULATION_GROWTH: 2.1,
    URBANIZATION_RATE: 28,
    DEPENDENCY_RATIO: 65,
    INTERNET_PENETRATION: 35,
    MOBILE_SUBSCRIPTIONS: 115,
    DIGITAL_PAYMENTS_GROWTH: 25,
    TECH_SECTOR_SIZE: 1.5,
    ECOMMERCE_PENETRATION: 2,

    // Investment Climate
    REGULATORY_QUALITY: 22,
    GOVERNMENT_EFFECTIVENESS: 18,
    POLICY_CONTINUITY: 6.0,
    INVESTOR_PROTECTION: 40,
    MARKET_SIZE_GDP: 11,
    TRADE_OPENNESS: 68,
    FTA_NETWORK: 5,
    REGIONAL_HUB_POTENTIAL: 4.5,
    DAYS_START_BUSINESS: 12,
    TAX_BURDEN: 35,
    INFRASTRUCTURE_QUALITY: 2.1,
    TERTIARY_ENROLLMENT: 32,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 3,
    REFORM_QUALITY: 4.5,
    REFORM_PIPELINE: 4.5,
    ANNOUNCED_FDI: 0.3,
    PIPELINE_CONVERSION: 50,
    INVESTOR_SENTIMENT: 4.5,
    RATING_UPGRADE_POTENTIAL: 4.0,
    ACCESSION_EVENTS: 2.0,
    POLITICAL_CALENDAR: 6.0,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 30,
    CONFLICT_RISK: 38,
    GEOPOLITICAL_RISK: 4.5,
    INFLATION_CPI: 8.5,
    CURRENCY_VOLATILITY: 15,
    EXTERNAL_DEBT_GDP: 45,
    RESERVE_ADEQUACY: 3,
    CURRENT_ACCOUNT: -4.0,
    SHORT_TERM_DEBT_RESERVES: 28,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 70,
    GROWTH_VS_RATING: 12,
    COST_ADVANTAGE: 8.5,
    SCORE_MOMENTUM: 1.0,
    CATALYST_PROXIMITY: 200,
    INVESTOR_POSITIONING: 6.0,
  },
};

// ============================================================================
// TURKMENISTAN - CENTRAL ASIAN PEER
// ============================================================================

export const TURKMENISTAN_DATA: CountryIndicatorData = {
  countryCode: 'TKM',
  countryName: 'Turkmenistan',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 5.5,
    GDP_GROWTH_ACCELERATION: -0.5,
    GDP_GROWTH_STABILITY: 3.2,
    SECTOR_DIVERSIFICATION: 0.55,
    PRODUCTIVITY_GROWTH: 2.0,
    WORKING_AGE_POP: 68,
    MEDIAN_AGE: 28,
    POPULATION_GROWTH: 1.5,
    URBANIZATION_RATE: 52,
    DEPENDENCY_RATIO: 48,
    INTERNET_PENETRATION: 25,
    MOBILE_SUBSCRIPTIONS: 165,
    DIGITAL_PAYMENTS_GROWTH: 15,
    TECH_SECTOR_SIZE: 1.0,
    ECOMMERCE_PENETRATION: 1,

    // Investment Climate
    REGULATORY_QUALITY: 8,
    GOVERNMENT_EFFECTIVENESS: 12,
    POLICY_CONTINUITY: 7.5,
    INVESTOR_PROTECTION: 25,
    MARKET_SIZE_GDP: 58,
    TRADE_OPENNESS: 45,
    FTA_NETWORK: 3,
    REGIONAL_HUB_POTENTIAL: 5.0,
    DAYS_START_BUSINESS: 15,
    TAX_BURDEN: 25,
    INFRASTRUCTURE_QUALITY: 2.4,
    TERTIARY_ENROLLMENT: 10,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 2,
    REFORM_QUALITY: 3.5,
    REFORM_PIPELINE: 3.0,
    ANNOUNCED_FDI: 1.5,
    PIPELINE_CONVERSION: 45,
    INVESTOR_SENTIMENT: 3.5,
    RATING_UPGRADE_POTENTIAL: 3.0,
    ACCESSION_EVENTS: 2.0,
    POLITICAL_CALENDAR: 7.0,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 35,
    CONFLICT_RISK: 55,
    GEOPOLITICAL_RISK: 5.5,
    INFLATION_CPI: 15.0,
    CURRENCY_VOLATILITY: 25,
    EXTERNAL_DEBT_GDP: 30,
    RESERVE_ADEQUACY: 8,
    CURRENT_ACCOUNT: 2.0,
    SHORT_TERM_DEBT_RESERVES: 15,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 50,
    GROWTH_VS_RATING: 8,
    COST_ADVANTAGE: 6.0,
    SCORE_MOMENTUM: 0.5,
    CATALYST_PROXIMITY: 300,
    INVESTOR_POSITIONING: 7.0,
  },
};

// ============================================================================
// AZERBAIJAN - CAUCASUS/REGIONAL PEER
// ============================================================================

export const AZERBAIJAN_DATA: CountryIndicatorData = {
  countryCode: 'AZE',
  countryName: 'Azerbaijan',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 2.5,
    GDP_GROWTH_ACCELERATION: 1.5,
    GDP_GROWTH_STABILITY: 4.5,
    SECTOR_DIVERSIFICATION: 0.52,
    PRODUCTIVITY_GROWTH: 1.8,
    WORKING_AGE_POP: 70,
    MEDIAN_AGE: 32,
    POPULATION_GROWTH: 0.8,
    URBANIZATION_RATE: 56,
    DEPENDENCY_RATIO: 42,
    INTERNET_PENETRATION: 82,
    MOBILE_SUBSCRIPTIONS: 105,
    DIGITAL_PAYMENTS_GROWTH: 35,
    TECH_SECTOR_SIZE: 2.8,
    ECOMMERCE_PENETRATION: 6,

    // Investment Climate
    REGULATORY_QUALITY: 42,
    GOVERNMENT_EFFECTIVENESS: 45,
    POLICY_CONTINUITY: 7.5,
    INVESTOR_PROTECTION: 55,
    MARKET_SIZE_GDP: 78,
    TRADE_OPENNESS: 75,
    FTA_NETWORK: 7,
    REGIONAL_HUB_POTENTIAL: 7.0,
    DAYS_START_BUSINESS: 4,
    TAX_BURDEN: 40,
    INFRASTRUCTURE_QUALITY: 3.2,
    TERTIARY_ENROLLMENT: 28,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 5,
    REFORM_QUALITY: 5.5,
    REFORM_PIPELINE: 5.5,
    ANNOUNCED_FDI: 2.5,
    PIPELINE_CONVERSION: 60,
    INVESTOR_SENTIMENT: 5.5,
    RATING_UPGRADE_POTENTIAL: 5.0,
    ACCESSION_EVENTS: 3.0,
    POLITICAL_CALENDAR: 6.5,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 32,
    CONFLICT_RISK: 35,
    GEOPOLITICAL_RISK: 4.5,
    INFLATION_CPI: 12.5,
    CURRENCY_VOLATILITY: 8,
    EXTERNAL_DEBT_GDP: 22,
    RESERVE_ADEQUACY: 12,
    CURRENT_ACCOUNT: 8.0,
    SHORT_TERM_DEBT_RESERVES: 20,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 75,
    GROWTH_VS_RATING: -5,
    COST_ADVANTAGE: 5.5,
    SCORE_MOMENTUM: 1.5,
    CATALYST_PROXIMITY: 180,
    INVESTOR_POSITIONING: 5.0,
  },
};

// ============================================================================
// MONGOLIA - REGIONAL PEER
// ============================================================================

export const MONGOLIA_DATA: CountryIndicatorData = {
  countryCode: 'MNG',
  countryName: 'Mongolia',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 5.2,
    GDP_GROWTH_ACCELERATION: 2.0,
    GDP_GROWTH_STABILITY: 5.0,
    SECTOR_DIVERSIFICATION: 0.48,
    PRODUCTIVITY_GROWTH: 3.0,
    WORKING_AGE_POP: 68,
    MEDIAN_AGE: 28,
    POPULATION_GROWTH: 1.5,
    URBANIZATION_RATE: 68,
    DEPENDENCY_RATIO: 52,
    INTERNET_PENETRATION: 75,
    MOBILE_SUBSCRIPTIONS: 135,
    DIGITAL_PAYMENTS_GROWTH: 42,
    TECH_SECTOR_SIZE: 3.0,
    ECOMMERCE_PENETRATION: 8,

    // Investment Climate
    REGULATORY_QUALITY: 45,
    GOVERNMENT_EFFECTIVENESS: 42,
    POLICY_CONTINUITY: 5.5,
    INVESTOR_PROTECTION: 58,
    MARKET_SIZE_GDP: 18,
    TRADE_OPENNESS: 115,
    FTA_NETWORK: 4,
    REGIONAL_HUB_POTENTIAL: 5.0,
    DAYS_START_BUSINESS: 8,
    TAX_BURDEN: 25,
    INFRASTRUCTURE_QUALITY: 2.5,
    TERTIARY_ENROLLMENT: 65,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 5,
    REFORM_QUALITY: 5.5,
    REFORM_PIPELINE: 5.5,
    ANNOUNCED_FDI: 1.8,
    PIPELINE_CONVERSION: 55,
    INVESTOR_SENTIMENT: 5.5,
    RATING_UPGRADE_POTENTIAL: 5.5,
    ACCESSION_EVENTS: 3.0,
    POLITICAL_CALENDAR: 5.0,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 55,
    CONFLICT_RISK: 72,
    GEOPOLITICAL_RISK: 6.0,
    INFLATION_CPI: 10.5,
    CURRENCY_VOLATILITY: 12,
    EXTERNAL_DEBT_GDP: 220,
    RESERVE_ADEQUACY: 5,
    CURRENT_ACCOUNT: -12.0,
    SHORT_TERM_DEBT_RESERVES: 45,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 180,
    GROWTH_VS_RATING: 15,
    COST_ADVANTAGE: 6.5,
    SCORE_MOMENTUM: 2.5,
    CATALYST_PROXIMITY: 90,
    INVESTOR_POSITIONING: 6.0,
  },
};

export const GEORGIA_DATA: CountryIndicatorData = {
  countryCode: 'GEO',
  countryName: 'Georgia',
  asOfDate: new Date('2024-12-01'),
  indicators: {
    // Growth Drivers
    GDP_GROWTH_5Y: 5.0,
    GDP_GROWTH_ACCELERATION: 2.0,
    GDP_GROWTH_STABILITY: 3.5,
    SECTOR_DIVERSIFICATION: 0.35,
    PRODUCTIVITY_GROWTH: 3.0,
    WORKING_AGE_POP: 65,
    MEDIAN_AGE: 38,
    POPULATION_GROWTH: -0.3,
    URBANIZATION_RATE: 60,
    DEPENDENCY_RATIO: 55,
    INTERNET_PENETRATION: 82,
    MOBILE_SUBSCRIPTIONS: 130,
    DIGITAL_PAYMENTS_GROWTH: 40,
    TECH_SECTOR_SIZE: 5.0,
    ECOMMERCE_PENETRATION: 12,

    // Investment Climate
    REGULATORY_QUALITY: 78,
    GOVERNMENT_EFFECTIVENESS: 72,
    POLICY_CONTINUITY: 6.5,
    INVESTOR_PROTECTION: 80,
    MARKET_SIZE_GDP: 28,
    TRADE_OPENNESS: 110,
    FTA_NETWORK: 10,
    REGIONAL_HUB_POTENTIAL: 6.5,
    DAYS_START_BUSINESS: 2,
    TAX_BURDEN: 20,
    INFRASTRUCTURE_QUALITY: 2.7,
    TERTIARY_ENROLLMENT: 70,

    // Catalyst Pipeline
    REFORM_COUNT_2Y: 5,
    REFORM_QUALITY: 6.0,
    REFORM_PIPELINE: 5.5,
    ANNOUNCED_FDI: 1.5,
    PIPELINE_CONVERSION: 60,
    INVESTOR_SENTIMENT: 6.0,
    RATING_UPGRADE_POTENTIAL: 5.5,
    ACCESSION_EVENTS: 6.0,
    POLITICAL_CALENDAR: 5.0,

    // Risk Profile
    POLITICAL_STABILITY_WGI: 38,
    CONFLICT_RISK: 45,
    GEOPOLITICAL_RISK: 5.0,
    INFLATION_CPI: 2.5,
    CURRENCY_VOLATILITY: 10,
    EXTERNAL_DEBT_GDP: 95,
    RESERVE_ADEQUACY: 4,
    CURRENT_ACCOUNT: -5.0,
    SHORT_TERM_DEBT_RESERVES: 40,

    // Valuation Signal
    FDI_GDP_VS_PEERS: 160,
    GROWTH_VS_RATING: 8,
    COST_ADVANTAGE: 6.5,
    SCORE_MOMENTUM: 1.0,
    CATALYST_PROXIMITY: 120,
    INVESTOR_POSITIONING: 5.5,
  },
};

// ============================================================================
// COUNTRY DATA REGISTRY - CENTRAL ASIA FOCUSED
// ============================================================================

export const COUNTRY_DATA: Record<string, CountryIndicatorData> = {
  // Primary Focus
  UZB: UZBEKISTAN_DATA,
  // Central Asian Peers
  KAZ: KAZAKHSTAN_DATA,
  KGZ: KYRGYZSTAN_DATA,
  TJK: TAJIKISTAN_DATA,
  TKM: TURKMENISTAN_DATA,
  // Caucasus/Regional Peers
  AZE: AZERBAIJAN_DATA,
  GEO: GEORGIA_DATA,
  // Regional Peer
  MNG: MONGOLIA_DATA,
};

/**
 * Get country data by code
 */
export function getCountryData(countryCode: string): CountryIndicatorData | undefined {
  return COUNTRY_DATA[countryCode];
}

/**
 * Get all available country codes
 */
export function getAvailableCountries(): string[] {
  return Object.keys(COUNTRY_DATA);
}

/**
 * Check if country data is available
 */
export function hasCountryData(countryCode: string): boolean {
  return countryCode in COUNTRY_DATA;
}

/**
 * Get country adjustments
 */
export function getCountryAdjustments(countryCode: string): ScoreAdjustment[] {
  switch (countryCode) {
    case 'UZB':
      return UZBEKISTAN_ADJUSTMENTS;
    default:
      return [];
  }
}
